/**
 * river_geometry — the PURE geometry the river renderer and its harnesses
 * share (2026-09-11, "rivers are still floating above the terrain … the
 * water fall needs a particle effect … the water is going up the waterfall").
 *
 * THE RIBBON FOLLOWS THE LEDGE. The carve holds a waterfall reach's water at
 * the upstream level to the lip (t = 0.5) and drops it over FALL_LIP_RUN
 * (shared/terrain.ts riverCarveSeg); the ribbon used to draw a straight strip
 * from point to point, so it ran UNDER the held ground before the lip and IN
 * THE AIR after it — the floating water in the user's screenshots. The rows
 * here break the strip at the lip and the foot; the sheet covers the drop.
 *
 * THE SHEET IS A NAPPE. Water leaving a lip is a projectile: s = v0·t,
 * y = −½·g·t² (the free-fall parabola of a plunge fall; a slack river still
 * arcs at NAPPE_V0_MIN). The sheet is that curve, subdivided, landing inside
 * the carve's run so it meets the ledge's foot.
 */
import { FALL_LIP_RUN } from '../../../shared/terrain.js';
import { RIVER_STRIDE, RIVER_FLAG_FALL, RIVER_FLAG_LAKE } from './hydrology_bake.js';

export const G_FALL = 9.81;
/** Rows down the free-fall curve (the sheet's vertical detail). */
export const NAPPE_ROWS = 14;
/** Vertices across the sheet. */
export const NAPPE_COLS = 6;
/** The water leaves the lip at least this fast (m/s). */
export const NAPPE_V0_MIN = 1.6;
/** The sheet dips this far under the pool surface so its seam hides in the foam. */
export const NAPPE_DIP = 0.35;

/** The carve's water surface along a reach (mirrors riverCarveSeg). */
export function reachWater(wa: number, wb: number, len: number, fall: boolean, t: number): number {
  if (!fall) return wa + (wb - wa) * t;
  const run = len > 0 ? FALL_LIP_RUN / len : 1;
  const u = t <= 0.5 ? 0 : t >= 0.5 + run ? 1 : (t - 0.5) / run;
  return wa + (wb - wa) * (u * u * (3 - 2 * u));
}

/** Where the ribbon breaks around a fall: the lip and the foot, as reach parameters. */
export function fallBreak(len: number): { lipT: number; footT: number } {
  return { lipT: 0.5, footT: Math.min(1, 0.5 + (len > 0 ? FALL_LIP_RUN / len : 1)) };
}

export interface RibbonRow {
  x: number; z: number;
  /** The water surface (no lift applied). */
  y: number;
  /** Half-width incl. the lip. */
  hw: number;
  /** Unit downstream tangent. */
  tx: number; tz: number;
  /** Bed depth mid-stream. */
  depth: number;
  /** false = the strip is NOT joined to the previous row (a fall's drop — the sheet covers it). */
  joined: boolean;
  /** Reach parameter along the segment starting at point k (0 at a point). */
  t: number;
}

/**
 * The ribbon's rows over the consecutive points run[] of river r: one row per
 * point and, for a fall reach, the LIP row (held level) and the FOOT row
 * (downstream level, FALL_LIP_RUN on) with the strip cut between them.
 */
export function ribbonRows(r: number[], run: number[], lip: number): RibbonRow[] {
  const rows: RibbonRow[] = [];
  const at = (k: number, c: number): number => r[k * RIVER_STRIDE + c];
  for (let i = 0; i < run.length; i++) {
    const k = run[i];
    const kp = run[Math.max(0, i - 1)], kn = run[Math.min(run.length - 1, i + 1)];
    let tx = at(kn, 0) - at(kp, 0), tz = at(kn, 1) - at(kp, 1);
    const tl = Math.hypot(tx, tz) || 1;
    tx /= tl; tz /= tl;
    rows.push({ x: at(k, 0), z: at(k, 1), y: at(k, 2), hw: at(k, 3) * 0.5 + lip, tx, tz, depth: at(k, 4), joined: true, t: 0 });
    if (i < run.length - 1 && (at(k, 5) & RIVER_FLAG_FALL) !== 0) {
      const b = run[i + 1];
      let dx = at(b, 0) - at(k, 0), dz = at(b, 1) - at(k, 1);
      const len = Math.hypot(dx, dz) || 1;
      dx /= len; dz /= len;
      const { lipT, footT } = fallBreak(len);
      const hwAt = (t: number): number => (at(k, 3) + (at(b, 3) - at(k, 3)) * t) * 0.5 + lip;
      const depthAt = (t: number): number => at(k, 4) + (at(b, 4) - at(k, 4)) * t;
      rows.push({ x: at(k, 0) + dx * len * lipT, z: at(k, 1) + dz * len * lipT, y: at(k, 2), hw: hwAt(lipT), tx: dx, tz: dz, depth: depthAt(lipT), joined: true, t: lipT });
      rows.push({ x: at(k, 0) + dx * len * footT, z: at(k, 1) + dz * len * footT, y: at(b, 2), hw: hwAt(footT), tx: dx, tz: dz, depth: depthAt(footT), joined: false, t: footT });
    }
  }
  return rows;
}

export interface NappePoint {
  /** Horizontal metres from the lip along the flow. */
  s: number;
  /** Metres below the lip (≤ 0). */
  y: number;
  /** Arc length from the lip (m) — the sheet's v coordinate. */
  v: number;
}

/**
 * The free-fall curve from the lip to the pool `drop` metres below: the water
 * leaves at v0 (≥ NAPPE_V0_MIN), throttled so it lands inside FALL_LIP_RUN
 * (where the carve's ledge foot is). `rows` samples from the lip (row 0) to the
 * pool surface.
 */
export function nappeCurve(drop: number, v0 = NAPPE_V0_MIN, rows = NAPPE_ROWS): { pts: NappePoint[]; v0: number; landing: number; tEnd: number } {
  const d = Math.max(0.1, drop);
  const tEnd = Math.sqrt((2 * d) / G_FALL);
  const vUse = Math.min(Math.max(v0, NAPPE_V0_MIN), (FALL_LIP_RUN * 0.85) / tEnd);
  const pts: NappePoint[] = [];
  let v = 0;
  for (let i = 0; i <= rows; i++) {
    const t = tEnd * (i / rows);
    const s = vUse * t, y = -0.5 * G_FALL * t * t;
    if (i > 0) v += Math.hypot(s - pts[i - 1].s, y - pts[i - 1].y);
    pts.push({ s, y, v });
  }
  return { pts, v0: vUse, landing: pts[rows].s, tEnd };
}

export interface FallFxRecipe {
  sprayRate: number; sprayMax: number;
  mistRate: number; mistMax: number; mistSize: number;
  crestRate: number; crestMax: number;
}

/**
 * Particle budgets for one fall from its width and drop, scaled by the tier's
 * particleCap (0 = none — the weather's law). Rates × lifetimes stay under the
 * pools (spray ≤ 1.1 s, mist ≤ 4.5 s, crest ≤ 0.9 s).
 */
export function fallFxRecipe(width: number, drop: number, cap: number): FallFxRecipe | null {
  if (cap <= 0) return null;
  const w = Math.min(12, Math.max(1.5, width)), dr = Math.min(30, Math.max(3, drop));
  const sprayRate = Math.round((120 + 30 * w + 8 * dr) * cap);
  const mistRate = Math.round((12 + 2.5 * w + 1.2 * dr) * cap);
  const crestRate = Math.round((24 + 5 * w) * cap);
  return {
    sprayRate, sprayMax: Math.ceil(sprayRate * 1.15) + 32,
    mistRate, mistMax: Math.ceil(mistRate * 4.6) + 16, mistSize: 1.2 + Math.min(2.4, dr * 0.09),
    crestRate, crestMax: Math.ceil(crestRate * 0.95) + 16,
  };
}

// ── R11 (2026-09-25): river v2 — the surface speed from Manning's equation ────
//
// v = (1/n) · R^(2/3) · S^(1/2), with the hydraulic radius R ≈ the depth (a
// channel many times wider than deep) and S the WATER SURFACE slope along the
// run. n = 0.035 is a clean natural channel with some stones (Chow 1959,
// Table 5-6: 0.030–0.040). Clamped 0.2–6 m/s so a still reach still drifts and
// a steep chute does not outrun the ribbon's detail. The banks run at 40 % of
// the centre (the no-slip wall — a real cross-section is parabolic). A fall's
// drop (an unjoined row) never enters a slope: the sheet carries that.

export const MANNING_N = 0.035;
export const RIVER_SPEED_MIN = 0.2;
export const RIVER_SPEED_MAX = 6;
export const RIVER_BANK_SPEED = 0.4;
/** Slopes are measured over ±SLOPE_WINDOW rows so one kinked vertex never reads as a rapid. */
export const SLOPE_WINDOW = 2;

export function manningSpeed(depth: number, slope: number): number {
  const d = Math.max(0.05, Number.isFinite(depth) ? depth : 0.05);
  const s = Math.max(1e-5, Math.min(0.2, Number.isFinite(slope) ? Math.abs(slope) : 0));
  const v = (1 / MANNING_N) * Math.pow(d, 2 / 3) * Math.sqrt(s);
  return Math.min(RIVER_SPEED_MAX, Math.max(RIVER_SPEED_MIN, v));
}

/** The Froude number the material thresholds (Fr > 0.7 streaks, Fr > 1 standing waves). */
export function froudeNumber(speed: number, depth: number): number {
  return speed / Math.sqrt(G_FALL * Math.max(0.05, depth));
}

/**
 * Per-row centre speeds for a ribbon run: the surface slope over a window that
 * never crosses an unjoined row (a fall's break), arc length from the rows'
 * own positions.
 */
export function rowSpeeds(rows: ReadonlyArray<Pick<RibbonRow, 'x' | 'z' | 'y' | 'depth' | 'joined'>>): number[] {
  const n = rows.length;
  const out = new Array<number>(n).fill(RIVER_SPEED_MIN);
  if (n === 0) return out;
  // Segment ids: a new segment starts at every unjoined row.
  const seg = new Array<number>(n);
  let id = 0;
  for (let i = 0; i < n; i++) { if (i > 0 && !rows[i].joined) id++; seg[i] = id; }
  const arc = new Array<number>(n).fill(0);
  for (let i = 1; i < n; i++) arc[i] = arc[i - 1] + Math.hypot(rows[i].x - rows[i - 1].x, rows[i].z - rows[i - 1].z);
  for (let i = 0; i < n; i++) {
    let a = i, b = i;
    for (let k = 1; k <= SLOPE_WINDOW; k++) { if (a - 1 >= 0 && seg[a - 1] === seg[i]) a--; if (b + 1 < n && seg[b + 1] === seg[i]) b++; }
    const len = arc[b] - arc[a];
    const slope = len > 0.5 ? (rows[a].y - rows[b].y) / len : 0;
    out[i] = manningSpeed(rows[i].depth, slope);
  }
  return out;
}

// ── R11 census + the rapids fixture (water session 3, 2026-09-26) ───────────
//
// "froudeShare 0" in the look probe was a FIXTURE, not a unit mismatch: the ribbon packs (bed depth m, Manning
// speed m/s) and the shader gates Fr = v / √(g·max(d, 0.05)) exactly as `froudeNumber` does — but the probe's
// river reach is a flat pool (57 % of the network's segments carry a level water surface, speed clamped to 0.2 m/s),
// so no vertex of THAT ribbon passed 0.7. Across the baked network 17.6 % of the ribbon rows pass 0.7 and 4.2 %
// pass 1 (verify:water-look pins the census). The probe now stands at the nearest supercritical reach instead.

/** One ribbon row with its Manning speed and Froude number (the renderer's own numbers). */
export interface FlowRow {
  x: number; z: number;
  /** Water surface (no lift). */
  water: number;
  /** The bed depth the shader reads (≥ the ribbon's edge depth). */
  depth: number;
  speed: number; froude: number;
  halfWidth: number; tx: number; tz: number;
  /** Index of the river in the network. */
  river: number;
  /** The Froude number of the row before / after on the same joined strip (NaN at a run's end or a fall's break). */
  prevFroude: number; nextFroude: number;
}

/**
 * Every ribbon row of a network as AtlasRiverModule draws it: runs of consecutive points with the lake segments
 * left out (a segment whose two points are both on a lake is the lake's to draw), the rows of `ribbonRows`, the
 * speeds of `rowSpeeds`, the depth floored at the ribbon's edge depth (`minDepth`, the module's RIBBON_EDGE_DEPTH).
 */
export function forEachFlowRow(rivers: ReadonlyArray<number[]>, visit: (row: FlowRow) => void, lip = 0.7, minDepth = 0.25): void {
  for (let ri = 0; ri < rivers.length; ri++) {
    const r = rivers[ri];
    const m = r.length / RIVER_STRIDE;
    const lake = (k: number): boolean => k >= 0 && k < m && (r[k * RIVER_STRIDE + 5] & RIVER_FLAG_LAKE) !== 0;
    let run: number[] = [];
    const flush = (): void => {
      if (run.length >= 2) {
        const rows = ribbonRows(r, run, lip);
        const sp = rowSpeeds(rows);
        const fr = rows.map((w, i) => froudeNumber(sp[i], Math.max(minDepth, w.depth)));
        for (let i = 0; i < rows.length; i++) {
          const w = rows[i];
          const depth = Math.max(minDepth, w.depth);
          const prevFroude = i > 0 && w.joined ? fr[i - 1] : NaN;
          const nextFroude = i + 1 < rows.length && rows[i + 1].joined ? fr[i + 1] : NaN;
          visit({ x: w.x, z: w.z, water: w.y, depth, speed: sp[i], froude: fr[i], halfWidth: w.hw, tx: w.tx, tz: w.tz, river: ri, prevFroude, nextFroude });
        }
      }
      run = [];
    };
    for (let k = 0; k < m; k++) {
      if (lake(k) && lake(k - 1)) { flush(); continue; }
      if (run.length === 0 && k > 0) run.push(k - 1);
      run.push(k);
    }
    flush();
  }
}

/**
 * The ribbon row nearest (x, z) whose Froude number passes `minFr` (null when none lies within `maxDist`). With
 * `stretch` (the default) both neighbours on the strip must pass 0.7 as well — a RUN of rapids a camera can look down
 * on, not a lone kinked row at a confluence (the WebGPU probe's first pick framed the tranquil river it joined).
 */
export function nearestRapid(rivers: ReadonlyArray<number[]>, x: number, z: number, minFr = 1, maxDist = Infinity, stretch = true): (FlowRow & { dist: number }) | null {
  let best: (FlowRow & { dist: number }) | null = null;
  forEachFlowRow(rivers, (row) => {
    if (!(row.froude >= minFr)) return;
    if (stretch && !(row.prevFroude > 0.7 && row.nextFroude > 0.7)) return;
    const d = Math.hypot(row.x - x, row.z - z);
    if (d <= maxDist && (!best || d < best.dist)) best = { ...row, dist: d };
  });
  return best;
}

/** The network's Froude census — the share of ribbon rows the white water can fire on. */
export function froudeCensus(rivers: ReadonlyArray<number[]>): { rows: number; over07: number; over1: number; max: number } {
  let rows = 0, over07 = 0, over1 = 0, max = 0;
  forEachFlowRow(rivers, (row) => { rows++; if (row.froude > 0.7) over07++; if (row.froude > 1) over1++; if (row.froude > max) max = row.froude; });
  return { rows, over07, over1, max };
}
